import { NgClass, NgStyle } from "@angular/common";
import {
    Component,
    ElementRef,
    Input,
    OnChanges,
    OnInit,
    SimpleChanges,
    ViewChild,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import _ from "lodash";
import { IMapGeometry } from "~app/common/components/map/map.settings";
import { TransportforetagService } from "~app/features/transport/transportforetag/services/transportforetag.service";
import { ITransportforetag } from "../../models/queries";
import { TplService } from "../../services/tpl.service";

export interface ILegendData {
    color: string;
    namn: string;
    symbolStyle: string;
}

@Component({
    selector: "rask-tpl-legend",
    templateUrl: "./tpl-legend.component.html",
    styleUrls: ["./tpl-legend.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        NgClass,
        NgStyle
    ]
})
export class TplLegendComponent implements OnInit, OnChanges {
    // eslint-disable-next-line @angular-eslint/no-input-rename
    @Input("data") data: IMapGeometry[];
    legendData: ILegendData[];

    @ViewChild("placeholder", { static: true }) private readonly placeholder: ElementRef;
    private readonly tplService = inject(TplService);
    private readonly transportforetagService = inject(TransportforetagService);

    private throttleCount = 0;
    private transportforetag: ITransportforetag[];

    isVisible() {
        return this.legendData && this.legendData.length > 0;
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes) {
            if (this.throttleCount === 0) {
                setTimeout(() => {
                    this.refresh();
                    this.throttleCount = 0;
                }, 500);
            } else {
                this.throttleCount++;
            }
        }
    }

    ngOnInit(): void {
        this.transportforetagService.getAllTransportforetag().subscribe(foretag => {
            this.transportforetag = foretag;
        });
    }

    refresh() {
        const result: ILegendData[] = [];
        const uniqeTrftg = _.uniqBy(this.data, g => g.tag.transportforetagId)
            .map(geometry => ({
                id: geometry.tag.transportforetagId as number,
                color: geometry.color,
                symbolStyle: geometry.symbolStyle
            }));
        uniqeTrftg.forEach(u => {
            const foretag = this.transportforetag.find(tf => tf.id === u.id);
            if (foretag) {
                result.push({
                    namn: foretag.namn,
                    color: u.color,
                    symbolStyle: u.symbolStyle
                });
            }
        });

        const geometriesWithIdenticalBefraktare = this.data.filter(g => {
            if (g.tag.handelssortiment.length === 0) {
                return false;
            }
            const firstBefraktareId = g.tag.handelssortiment[0].befraktareId;
            return g.tag.handelssortiment.every(hs => hs.befraktareId === firstBefraktareId);
        });

        const uniqBef = _.uniqBy(
            geometriesWithIdenticalBefraktare.flatMap(g =>
                g.tag.handelssortiment.map(hs => ({
                    id: hs.befraktareId,
                    color: g.color,
                    symbolStyle: g.symbolStyle
                }))
            ),
            x => x.id
        ).filter(x => !!x.id);

        uniqBef.forEach(b => {
            const namn = this.tplService.resolveBefraktareAndGetName(b.id);
            result.push({
                namn: namn,
                color: b.color,
                symbolStyle: b.symbolStyle
            });
        });

        this.legendData = _.take(result, 12);
    }
}
